/**
 * FinAPI Bank verbinden – eigenständige Tab-Seite (Buchhaltung)
 */
import { useState, useEffect } from 'react';
import { Link2, CheckCircle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useTabs } from '@/contexts/TabContext';
import { emitDataRefresh } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function FinApiPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};
  const konto = params.konto;

  const [loading, setLoading] = useState(false);
  const [banken, setBanken] = useState([]);
  const [selectedBank, setSelectedBank] = useState('');
  const [suche, setSuche] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/buchhaltung/finapi/banken`).then(r => r.json())
      .then(setBanken).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleConnect = async () => {
    if (!selectedBank) { toast.error('Bitte Bank auswählen'); return; }
    setLoading(true);
    try {
      const result = await fetch(`${BACKEND_URL}/api/buchhaltung/finapi/verbinden`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ konto_id: konto?.id, klient_id: params.klientId, blz: selectedBank })
      }).then(r => r.json());
      if (result.success) {
        toast.success(result.message);
        emitDataRefresh('buchhaltung');
        closeTab(activeTab.id);
      } else { toast.error(result.detail || 'Verbindung fehlgeschlagen'); }
    } catch { toast.error('Fehler bei Verbindung'); }
    setLoading(false);
  };

  const filteredBanken = suche ? banken.filter(b => b.name.toLowerCase().includes(suche.toLowerCase())) : banken;

  return (
    <div className="max-w-lg mx-auto" data-testid="finapi-page">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Link2 className="w-5 h-5 text-teal-600" />Bank verbinden (Mock)
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => closeTab(activeTab.id)}><X className="w-4 h-4" /></Button>
          </div>
          <CardDescription>
            Verbinden Sie das Konto "{konto?.bezeichnung}" mit Online-Banking.
            <span className="block text-amber-600 mt-1">Dies ist ein Simulator für Testzwecke.</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Bank suchen</Label>
            <Input value={suche} onChange={e => setSuche(e.target.value)} placeholder="z.B. Sparkasse, ING..." />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {filteredBanken.map(b => (
              <div key={b.blz}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedBank === b.blz ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                onClick={() => setSelectedBank(b.blz)}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-700">{b.name}</p>
                    <p className="text-xs text-slate-400">BLZ: {b.blz}</p>
                  </div>
                  {selectedBank === b.blz && <CheckCircle className="w-5 h-5 text-blue-500" />}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800"><strong>Hinweis:</strong> Dies ist ein Mock-Modus. Es werden simulierte Transaktionen generiert.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => closeTab(activeTab.id)}>Abbrechen</Button>
            <Button onClick={handleConnect} disabled={loading || !selectedBank} data-testid="connect-finapi">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verbinde...</> : <><Link2 className="w-4 h-4 mr-2" />Verbinden</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
